import React from "react";
import { useTranslationStore } from "../../../state/useTranslationStore";
import AddLanguageButton from "./AddLanguageButton";
import { toast } from "sonner";

// Map known prayer names to their shortcode tags
const PRAYER_NAME_SHORTCODES: Record<string, string> = {
  Fajr: "[Fp_Single_Fajr_Name]",
  Dhuhr: "[Fp_Single_Dhuhr_Name]",
  Asr: "[Fp_Single_Asr_Name]",
  Maghrib: "[Fp_Single_Maghrib_Name]",
  Isha: "[Fp_Single_Isha_Name]",
  Sunrise: "[Fp_Single_Sunrise_Name]",
  Midnight: "[Fp_Single_Midnight_Name]",
};
// Single fixed badge class for consistency
const badgeClass =
  "ml-2 inline-block px-2 py-0.5 text-xs rounded border bg-green-100 text-green-800 border-green-200";

const PrayerNamesEditor: React.FC = () => {
  const {
    prayer_names,
    activeLanguage,
    availableLanguages,
    loading,
    selectLanguage,
    updateTranslation,
    saveTranslations,
    addNewLanguage,
  } = useTranslationStore();

  // Filter prayer names for the active language and track original index
  const activePrayerNames = prayer_names
    .map((prayer, idx) => ({ ...prayer, originalIndex: idx }))
    .filter((prayer) => prayer.language_code === activeLanguage);

  // Handle input changes using original index
  const handleInputChange = (originalIndex: number, value: string) => {
    if (originalIndex >= 0 && originalIndex < prayer_names.length) {
      updateTranslation("prayer_names", originalIndex, value);
    } else {
      console.error("Invalid index for prayer_names");
    }
  };

  // Handle saving translations
  const handleSave = async () => {
    try {
      await saveTranslations();
      toast.success("Prayer names updated successfully!");
    } catch (error) {
      console.error("Error saving prayer names:", error);
      toast.error("Failed to save prayer names.");
    }
  };

  return (
    <div className="prayer-names-editor p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-green-600">Edit Prayer Names</h1>

      {/* Language Selection */}
      <div className="mb-4 flex gap-4 flex-wrap">
        <div className="space-x-3">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 capitalize ${
                activeLanguage === lang.code
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {lang.language}
            </button>
          ))}
        </div>
        <AddLanguageButton
          data={prayer_names}
          handlerAddLangauge={addNewLanguage}
          disabled={loading}
        />
      </div>

      {/* Translation Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activePrayerNames.map((prayer) => (
          <div
            key={`${prayer.original_text}-${prayer.language_code}`}
            className="flex flex-col gap-2"
          >
            <label className="text-gray-700 font-medium">
              {prayer.original_text} ({prayer.language_code})
              {PRAYER_NAME_SHORTCODES[prayer.original_text] && (
                <span className={badgeClass}>
                  {PRAYER_NAME_SHORTCODES[prayer.original_text]}
                </span>
              )}
            </label>
            <input
              type="text"
              value={prayer.translated_text}
              onChange={(e) => handleInputChange(prayer.originalIndex, e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default PrayerNamesEditor;
